import React, { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAccountSubscriptionsQuery } from "fond/api";
import { Maybe, MultiProject, Project, QuotaUsageDetails, SubscriptionStatus } from "fond/types";
import { isExtendedPlannerProject } from "fond/utils/project";
import { getDesignQuota, getPlannerQuota } from "fond/utils/subscription";

/**
 * Hook to return the relevant quota and quota usage for a given project.
 */
export const useProjectQuota = (project: Project | MultiProject | null): { quota: Maybe<QuotaUsageDetails>; isLoaded: boolean } => {
  const { data: subscriptions, isSuccess: isLoaded } = useGetAccountSubscriptionsQuery(project?.Account?.ID ?? skipToken);

  // "FOND design" (planner_extended) projects consume design quota, while other projects consume 'planner' quota.
  const quota: QuotaUsageDetails | null = useMemo(() => {
    const subscription = subscriptions?.find((sub) => sub.Status === SubscriptionStatus.Active);
    if (!subscription) return null;

    if (isExtendedPlannerProject(project)) {
      const { Quota, QuotaUsage, DataPackQuota, DataPackUsage, AvailableQuota, hasDesign } = getDesignQuota(subscription);
      if (!hasDesign) return null;

      return { Quota, QuotaUsage, DataPackQuota, DataPackUsage, AvailableQuota };
    }

    const { Quota, QuotaUsage, DataPackQuota, DataPackUsage, AvailableQuota, hasPlanner } = getPlannerQuota(subscription);
    if (!hasPlanner) return null;

    return { Quota, QuotaUsage, DataPackQuota, DataPackUsage, AvailableQuota };
  }, [project, subscriptions]);

  return useMemo(() => ({ quota, isLoaded }), [quota, isLoaded]);
};
